import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { PaperAirplaneIcon, ChatBubbleLeftRightIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, Avatar, PageLoader, EmptyState } from '../../components/ui';
import { chatService, projectService } from '../../services';
import { useAuthStore } from '../../stores';
import { useSocket } from '../../hooks';

export default function Chat() {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const { user } = useAuthStore();
  const { socket, joinProject, leaveProject, sendMessage: sendSocketMessage, stopTyping } = useSocket();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectId || null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const messageIdsRef = useRef(new Set()); // Track received message IDs to prevent duplicates
  const hasMoreMessages = useRef(true);
  const pendingMessagesRef = useRef(new Map()); // Track pending temp messages by content+timestamp

  useEffect(() => {
    fetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProject) {
      // Reset state when switching projects
      setMessages([]);
      messageIdsRef.current.clear();
      pendingMessagesRef.current.clear();
      hasMoreMessages.current = true;
      setLoading(true);
      setError(null);

      fetchMessages();
      joinProject(selectedProject);

      // Mark messages as read when joining project
      if (socket) {
        socket.emit('chat:read', { projectId: selectedProject });
      }

      // Handle incoming real-time messages
      const handleMessage = (newMessage) => {
        // Prevent duplicate messages by ID
        if (messageIdsRef.current.has(newMessage._id)) {
          return;
        }
        messageIdsRef.current.add(newMessage._id);

        setMessages(prev => {
          // Check if message already exists (in case of race condition)
          if (prev.find(m => m._id === newMessage._id)) {
            return prev;
          }

          // If this is our own message, check if we have a pending temp message to replace
          if (newMessage.sender?._id === user?._id) {
            // Find and replace temp message with the same content
            const tempMessageIndex = prev.findIndex(m =>
              m._id?.startsWith('temp-') &&
              m.content === newMessage.content &&
              m.sender?._id === user?._id
            );

            if (tempMessageIndex !== -1) {
              // Replace temp message with real one
              const updated = [...prev];
              updated[tempMessageIndex] = newMessage;
              // Clean up pending message
              const tempId = prev[tempMessageIndex]._id;
              pendingMessagesRef.current.delete(tempId);
              return updated;
            }
          }

          // Otherwise, add as new message
          return [...prev, newMessage];
        });

        // Auto-scroll to bottom for new messages
        setTimeout(() => scrollToBottom(), 100);

        // Mark as read if it's not our own message
        if (newMessage.sender?._id !== user?._id && socket) {
          socket.emit('chat:read', {
            projectId: selectedProject,
            messageIds: [newMessage._id]
          });
        }
      };

      // Handle typing indicators
      const handleTyping = ({ user: typingUser, projectId: typingProjectId }) => {
        if (typingProjectId !== selectedProject || typingUser._id === user?._id) return;

        setTypingUsers(prev => {
          if (!prev.find(u => u._id === typingUser._id)) {
            return [...prev, typingUser];
          }
          return prev;
        });

        // Auto-remove after 3 seconds
        setTimeout(() => {
          setTypingUsers(prev => prev.filter(u => u._id !== typingUser._id));
        }, 3000);
      };

      // Handle stop typing
      const handleStopTyping = ({ userId, projectId: typingProjectId }) => {
        if (typingProjectId !== selectedProject) return;
        setTypingUsers(prev => prev.filter(u => u._id !== userId));
      };

      // Handle socket errors
      const handleError = (error) => {
        console.error('Chat socket error:', error);
        toast.error(error.message || t('errors.serverError'));
      };

      if (socket) {
        socket.on('chat:message', handleMessage);
        socket.on('chat:typing', handleTyping);
        socket.on('chat:stop-typing', handleStopTyping);
        socket.on('chat:error', handleError);
      }

      return () => {
        leaveProject(selectedProject);
        if (socket) {
          socket.off('chat:message', handleMessage);
          socket.off('chat:typing', handleTyping);
          socket.off('chat:stop-typing', handleStopTyping);
          socket.off('chat:error', handleError);
        }
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, socket, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchProjects = async () => {
    try {
      const response = await projectService.getProjects({ limit: 50 });
      setProjects(response.data);
      if (!selectedProject && response.data.length > 0) {
        setSelectedProject(response.data[0]._id);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      setError(null);
      const response = await chatService.getMessages(selectedProject, { limit: 50, page: 1 });
      const fetchedMessages = response.data || [];

      // Track message IDs to prevent duplicates
      fetchedMessages.forEach(msg => {
        messageIdsRef.current.add(msg._id);
      });

      setMessages(fetchedMessages);
      hasMoreMessages.current = fetchedMessages.length >= 50;

      // Mark as read via socket (more efficient)
      if (socket && fetchedMessages.length > 0) {
        const messageIds = fetchedMessages.map(m => m._id);
        socket.emit('chat:read', { projectId: selectedProject, messageIds });
      }

      // Scroll to bottom after loading
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 403) {
        setError({
          type: 'forbidden',
          message: t('chat.errors.noPermission') || 'You do not have permission to view messages in this project.',
          title: t('chat.errors.accessDenied') || 'Access Denied'
        });
      } else if (error.response?.status === 404) {
        setError({
          type: 'notFound',
          message: t('chat.errors.projectNotFound') || 'Project not found.',
          title: t('chat.errors.notFound') || 'Not Found'
        });
      } else {
        setError({
          type: 'server',
          message: error.response?.data?.message || t('errors.serverError') || 'Failed to load messages. Please try again.',
          title: t('chat.errors.loadFailed') || 'Failed to Load'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedProject || !socket) return;

    const messageContent = message.trim();
    setMessage('');
    setSending(true);

    // Clear typing indicator
    stopTyping(selectedProject);

    try {
      // Create temp message for optimistic update
      const tempId = `temp-${Date.now()}`;
      const tempMessage = {
        _id: tempId,
        sender: {
          _id: user._id,
          firstName: user.firstName,
          lastName: user.lastName,
          avatar: user.avatar
        },
        content: messageContent,
        createdAt: new Date()
      };

      // Track pending message
      pendingMessagesRef.current.set(tempId, {
        content: messageContent,
        timestamp: Date.now()
      });

      // Add temp message optimistically
      setMessages(prev => [...prev, tempMessage]);
      setTimeout(() => scrollToBottom(), 100);

      // Send via socket (more efficient - real-time)
      // The real message will replace the temp one when received
      sendSocketMessage(selectedProject, messageContent, []);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.message || t('errors.serverError'));
      // Restore message on error
      setMessage(messageContent);
    } finally {
      setSending(false);
    }
  };

  const getSelectedProjectName = () => {
    const project = projects.find(p => p._id === selectedProject);
    return project?.name || '';
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <>
      <Helmet>
        <title>{t('chat.title')} - ProCTM</title>
      </Helmet>

      <PageHeader title={t('chat.title')} />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-200px)]">
        {/* Project List */}
        <Card className="lg:col-span-1 overflow-hidden">
          <CardBody className="p-0 h-full overflow-y-auto">
            <div className="divide-y divide-gray-100">
              {projects.map((project) => (
                <button
                  key={project._id}
                  onClick={() => setSelectedProject(project._id)}
                  className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${selectedProject === project._id ? 'bg-primary/5 border-l-2 border-primary' : ''
                    }`}
                >
                  <p className="font-medium truncate">{project.name}</p>
                  <p className="text-sm text-gray-500">{project.client?.name || '-'}</p>
                </button>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Chat Area */}
        <Card className="lg:col-span-3 flex flex-col overflow-hidden">
          {selectedProject ? (
            <>
              {/* Chat Header */}
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="font-medium">{getSelectedProjectName()}</h3>
              </div>

              {/* Messages */}
              <CardBody className="flex-1 overflow-y-auto p-4 space-y-4">
                {error ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <div className="text-center p-6 bg-red-50 border border-red-200 rounded-lg max-w-md">
                      <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
                      <div className="text-red-600 font-semibold text-lg mb-2">
                        {error.title}
                      </div>
                      <p className="text-red-700 text-sm mb-4">
                        {error.message}
                      </p>
                      {error.type === 'forbidden' && (
                        <p className="text-gray-600 text-xs mb-4">
                          {t('chat.errors.contactAdmin') || 'Please contact the project owner or administrator for access.'}
                        </p>
                      )}
                    </div>
                  </div>
                ) : messages.length > 0 ? (
                  <>
                    {messages.map((msg, index) => {
                      const isOwn = msg.sender?._id === user?._id;
                      const showAvatar = index === 0 ||
                        messages[index - 1]?.sender?._id !== msg.sender?._id;

                      return (
                        <div
                          key={msg._id || index}
                          className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}
                        >
                          {showAvatar ? (
                            <Avatar
                              src={msg.sender?.avatar ? (msg.sender.avatar.startsWith('http') ? msg.sender.avatar : process.env.REACT_APP_SOCKET_URL + msg.sender.avatar) : null}
                              name={`${msg.sender?.firstName} ${msg.sender?.lastName}`}
                              size="sm"
                            />
                          ) : (
                            <div className="w-8" />
                          )}
                          <div className={`max-w-[70%] ${isOwn ? 'text-right' : ''}`}>
                            {showAvatar && (
                              <div className={`flex items-center gap-2 mb-1 ${isOwn ? 'flex-row-reverse' : ''}`}>
                                <span className="text-sm font-medium">
                                  {msg.sender?.firstName} {msg.sender?.lastName}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {dayjs(msg.createdAt).format('h:mm A')}
                                </span>
                              </div>
                            )}
                            <div className={`inline-block px-4 py-2 rounded-lg ${isOwn
                              ? 'bg-primary text-white'
                              : 'bg-gray-100 text-neutral-dark'
                              }`}>
                              <p className="text-sm">{msg.content}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                ) : (
                  <EmptyState
                    icon={ChatBubbleLeftRightIcon}
                    title={t('chat.noMessages')}
                    description={t('chat.startConversation')}
                  />
                )}

                {typingUsers.length > 0 && (
                  <div className="text-sm text-gray-500 italic">
                    {typingUsers.map(u => u.firstName).join(', ')} is typing...
                  </div>
                )}
              </CardBody>

              {/* Input */}
              {error ? null : (
                <div className="p-4 border-t border-gray-100">
                  <form onSubmit={handleSend} className="flex gap-3">
                    <input
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t('chat.typeMessage')}
                      className="input flex-1"
                      disabled={sending}
                    />
                    <Button type="submit" loading={sending} disabled={!message.trim()}>
                      <PaperAirplaneIcon className="w-5 h-5 rtl:rotate-180" />
                    </Button>
                  </form>
                </div>
              )}
            </>
          ) : (
            <CardBody className="flex-1 flex items-center justify-center">
              <EmptyState
                icon={ChatBubbleLeftRightIcon}
                title="Select a project"
                description="Choose a project to start chatting"
              />
            </CardBody>
          )}
        </Card>
      </div>
    </>
  );
}
